import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { IrisDMSFolder, IrisDMSFolderI, IrisDmsFolderResourceI } from '@iris/common/modules/dms/models/IrisDMSFolder';
import { IrisDMSFileI } from '@iris/common/modules/dms/models/IrisDMSFile';
import { map } from 'rxjs/operators';
import { ApiUrl, IrisApiClient } from '@iris/api-query';
import { IrisQueryParams, IrisQueryParamsBuilder, IrisQueryParamsOrderBy } from '@iris/api-query';
import { IrisDmsFolderInfoI } from '@iris/common/modules/dms/models/IrisDmsFolderInfo';
import { IrisDMSFolderOrFile } from '@iris/modules/dms/utils/file.utils';
import {
  IrisDMSProjectFolderSettingI,
} from '@iris/modules/system-configuration/system-configuration-list/documents/project-folder-page/IrisDMSProjectFolderSettings';
import { isBoolean } from 'lodash';
import { IrisProjectDmsSettingsI } from '@iris/common/modules/dms/models/IrisProjectDmsSettings';
import saveAs from 'file-saver';
import { fullIrisPage, IrisPage } from '@iris/common/models/page';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';

export interface IrisDmsContentPageI {
  files: IrisDMSFileI[];
  folders: IrisDMSFolderI[];
  count?: number;
}

export interface IrisDmsPaginationI<T = IrisDMSFolderOrFile> {
  count?: number;
  elements: T[];
}

export interface IrisDmsContentRequestParamsI {
  folderId: string;
  offset: number;
  limit?: number;
  orderBy?: IrisQueryParamsOrderBy[];
  excludeHistory: boolean;
  onlyFiles?: boolean;
  filter?: IrisQueryParams;
}

export interface IrisFolderProjectStructureI {
  folderId: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
@ApiUrl('/dms2/{iris@instanceId}/folders')
export class IrisFoldersService extends IrisApiClient<IrisDMSFolderI> {
  protected readonly httpClient: HttpClient;
  private readonly globalSandbox = inject(IrisGlobalSandbox);

  constructor() {
    const httpClient = inject(HttpClient);

    super(httpClient);

    this.httpClient = httpClient;
  }

  public getFolders(params: IrisQueryParams = new IrisQueryParams()): Observable<IrisDMSFolderI[]> {
    return this.httpClient.get<IrisDMSFolder[]>(this.url(), { params: params.toObject() });
  }

  @ApiUrl('~/projects/{projectId}/root')
  public getProjectRootFolder(projectId: number): Observable<IrisDMSFolderI> {
    if (!projectId) {
      return of(null);
    }
    return this.httpClient.get<IrisDMSFolderI>(this.url({ projectId }));
  }

  @ApiUrl('~/projects/{projectId}')
  public getProjectSubFolders(projectId: number): Observable<IrisDMSFolderI[]> {
    return this.httpClient.get<IrisDMSFolderI[]>(this.url({ projectId }));
  }

  @ApiUrl('~/projects/default/project-folder-settings')
  public getProjectFolderDefaultSettings(): Observable<IrisDMSProjectFolderSettingI> {
    return this.httpClient.get<IrisDMSProjectFolderSettingI>(this.url());
  }

  @ApiUrl('~/projects/default/project-folder-settings')
  public saveProjectFolderDefaultSettings(settings: IrisDMSProjectFolderSettingI): Observable<IrisDMSProjectFolderSettingI> {
    return this.httpClient.post<IrisDMSProjectFolderSettingI>(this.url(), settings);
  }

  @ApiUrl('~/projects/{projectId}/project-folder-settings')
  public getProjectFolderSettings(projectId: number | string = 'default'): Observable<IrisProjectDmsSettingsI> {
    return this.httpClient.get<IrisProjectDmsSettingsI>(this.url({ projectId }));
  }

  @ApiUrl('~/projects/{projectId}/project-folder-settings')
  public saveProjectFolderSettings(projectId: number, settings: IrisProjectDmsSettingsI): Observable<IrisProjectDmsSettingsI> {
    return this.httpClient.post<IrisProjectDmsSettingsI>(this.url({ projectId }), settings);
  }

  @ApiUrl('~/projects/{projectId}/path')
  public getProjectSubFolderByPath(projectId: number, folderPath: string, isCreate: boolean): Observable<IrisDMSFolderI> {
    return this.httpClient.get<IrisDMSFolderI>(this.url({ projectId }), { params: { folderPath, isCreate } });
  }

  @ApiUrl('~/path')
  public getProjectSubFolderByPattern(pattern: string): Observable<IrisDMSFolderI[]> {
    return this.httpClient.get<IrisDMSFolderI[]>(this.url(), { params: { pattern } } );
  }

  @ApiUrl(`~/page?system=true`)
  public getSystemFolders(params: IrisQueryParams = new IrisQueryParams()): Observable<IrisDmsPaginationI<IrisDMSFolderI>> {
    return this.httpClient.get<IrisDmsPaginationI<IrisDMSFolderI>>(this.url(), { params: params.toObject() });
  }

  public goToDMS(folderId: string): string {
    return `/${this.globalSandbox.appAlias()}/dms/${this.globalSandbox.currentProjectId()}/folders/${folderId}/files`;
  }

  public getFolderSourceUrl(folderId: string): string {
    return `${location.origin}/${this.globalSandbox.appAlias()}/dms/${this.globalSandbox.currentProjectId()}/folders/${folderId}/files`; // pageUrl from config is not relevant e.g. if we start from profile page
  }

  @ApiUrl('~/{folderId}')
  public getFolderById(folderId: string): Observable<IrisDMSFolderI> {
    return this.httpClient.get<IrisDMSFolderI>(this.url({ folderId }));
  }

  @ApiUrl('~/{folderId}/files')
  public getFilesByFolderId(folderId: string, params: IrisQueryParams = new IrisQueryParams() ): Observable<IrisDMSFileI[]> {
    return this.httpClient.get<IrisDMSFileI[]>(this.url({ folderId }), { params: params.toObject() });
  }

  @ApiUrl(`~/{folderId}/files/sia`)
  public uploadSiaFiles(folderId: string, siaFilesIds: string[]): Observable<IrisDMSFileI[]> {
    return this.httpClient.post<IrisDMSFileI[]>(this.url({ folderId }), { files: siaFilesIds });
  }

  @ApiUrl('~/{folderId}/toggle-locked')
  public toggleLockFolder(folderId: string): Observable<IrisDMSFolderI> {
    return this.httpClient.post<IrisDMSFolderI>(this.url({ folderId }), { id: folderId });
  }

  @ApiUrl('~/{folderId}/parents')
  public getParentFolders(folderId: string): Observable<IrisDMSFolderI> {
    return this.httpClient.get<IrisDMSFolderI>(this.url({ folderId }));
  }

  public createFolder(folder: Partial<IrisDMSFolderI>): Observable<IrisDMSFolderI> {
    return this.httpClient.post<IrisDMSFolderI>(this.url(), folder);
  }

  @ApiUrl('~/{folderId}/download')
  downloadFolder(folderId: string, folderName?: string): Observable<unknown> {
    return this.httpClient.get(this.url({ folderId }), { responseType: 'blob' }).pipe(
      map(response => {
        saveAs(response, `${folderName}.zip`);
        return null;
      }),
    );
  }

  @ApiUrl('~/{folderId}')
  public updateFolder(folder: Partial<IrisDMSFolderI>): Observable<IrisDMSFolderI> {
    return this.httpClient.post<IrisDMSFolderI>(this.url({ folderId: folder.id }), folder);
  }

  @ApiUrl('~/{folderId}')
  public deleteFolder(folderId: string): Observable<void> {
    return this.httpClient.delete<void>(this.url({ folderId }));
  }

  @ApiUrl('~/{folderId}/files/page')
  public getContentPage(params: IrisDmsContentRequestParamsI): Observable<IrisDmsContentPageI> {
    const { folderId, offset, limit, orderBy, filter } = params;
    let { excludeHistory, onlyFiles } = params;
    excludeHistory = isBoolean(excludeHistory) ? excludeHistory : true;
    onlyFiles = isBoolean(onlyFiles) ? onlyFiles : false;
    const extendedParams = new IrisQueryParamsBuilder(filter ?? {}).urlParam('offset', offset);

    if (limit) {
      extendedParams.limit(limit);
    }
    if (orderBy?.length) {
      extendedParams.urlParam('order-by', orderBy);
    }
    if (excludeHistory) {
      extendedParams.excludeFields(['history']);
    }
    if (onlyFiles) {
      extendedParams.urlParam('only-files', true);
    }

    return this.httpClient.get<IrisDmsContentPageI>(this.url({ folderId }), { params: extendedParams.toObject() });
  }

  @ApiUrl('~/{folderId}/info?fileCount={fileCount}&folderCount={folderCount}&folderSize={folderSize}')
  public getFolderInfo(
    folderId: string,
    fileCount = true,
    folderCount = true,
    folderSize = true,
    isFilesInAllSubFoldersCount = true,
  ): Observable<IrisDmsFolderInfoI> {
    return this.httpClient.get<IrisDmsFolderInfoI>(this.url({
      folderId,
      fileCount,
      folderCount,
      folderSize,
      isFilesInAllSubFoldersCount,
    }));
  }

  @ApiUrl('~/{folderId}/exist?name={name}')
  public checkFolderExists(folderId: string, name: string): Observable<boolean> {
    return this.httpClient.get<boolean>(this.url({ folderId, name }));
  }

  @ApiUrl('~/{folderId}/sync-exist')
  public checkSyncConfigurationExists(folderId: string): Observable<boolean> {
    return this.httpClient.get<boolean>(this.url({ folderId }));
  }

  @ApiUrl('~/{folderId}/unzip-file/{fileId}')
  public unzipFile(folderId: string, fileId: string): Observable<unknown> {
    return this.httpClient.post<unknown>(this.url({ folderId, fileId }), null);
  }

  @ApiUrl('~/{module}?subject={subject}')
  getModuleFileUploadUrl(module: string, subject): string {
    return this.fullUrl({ module, subject: encodeURIComponent(JSON.stringify(subject)) });
  }

  @ApiUrl('~/projects/folder-structures')
  getAvailableFolderStructures(): Observable<IrisFolderProjectStructureI[]> {
    return this.httpClient.get<IrisFolderProjectStructureI[]>(this.url());
  }

  @ApiUrl('/security/dms-folders')
  getFoldersPermissionsResouerces(): Observable<IrisPage<IrisDmsFolderResourceI>> {
    return this.httpClient.get<IrisDmsFolderResourceI[]>(this.url()).pipe(
      map(resoures => fullIrisPage<IrisDmsFolderResourceI>(resoures)),
    );
  }

  @ApiUrl('/security/dms-folders')
  createFoldersPermissionsResource(path: string): Observable<IrisDmsFolderResourceI> {
    return this.httpClient.post<IrisDmsFolderResourceI>(this.url(), { path });
  }

  @ApiUrl('/security/dms-folders/{resourceId}')
  removeFoldersPermissionsResource(resourceId: number): Observable<void> {
    return this.httpClient.delete<void>(this.url({ resourceId }));
  }
}
